/**
 * pages/Accounts.js  — Phase 4B.
 *
 * Unified home for all financial-account management. Five top tabs:
 *
 *   Overview · Cash · Investments · Liabilities · Recurring
 *
 * Reads authoritative data from three tables (accounts, wealth_holdings,
 * liabilities) via `useAccountsData`. All Add / Edit / Delete / Set-balance
 * actions round-trip to the correct underlying table. Recurring is the
 * legacy `pages/Recurring.js` embedded in the tab (no logic duplication).
 *
 * URL:
 *   /accounts                   → default (Overview)
 *   /accounts?tab=cash          → deep-link
 *   /accounts?tab=investments   → deep-link
 *   /accounts?tab=liabilities   → deep-link
 *   /accounts?tab=recurring     → deep-link
 *
 * The legacy `/wealth`, `/liabilities`, `/recurring` routes redirect here
 * (see App.js).
 *
 * Phase 4B is a UI + IA unification. No DB schema changes.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Upload, Plus, Loader2, RefreshCcw } from 'lucide-react';
import { toast } from 'sonner';
import usePageTitle from '@/hooks/usePageTitle';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import { Button } from '@/components/ui/button';

import useAccountsData      from '@/components/accounts/useAccountsData';
import { useCurrencyContext } from '@/hooks/useCurrencyContext';
import OverviewTab          from '@/components/accounts/OverviewTab';
import CategoryTab          from '@/components/accounts/CategoryTab';
import CashDetail           from '@/components/accounts/CashDetail';
import InvestmentDetail     from '@/components/accounts/InvestmentDetail';
import LiabilityDetail      from '@/components/accounts/LiabilityDetail';
import AccountFormDialog    from '@/components/accounts/AccountFormDialog';
import SetBalanceDialog     from '@/components/accounts/SetBalanceDialog';
import DeleteAccountDialog  from '@/components/accounts/DeleteAccountDialog';
import DebtFocusTable       from '@/components/liabilities/DebtFocusTable';
import { DebtPlanProvider } from '@/components/liabilities/DebtPlanContext';
import DebtKpiStrip        from '@/components/liabilities/DebtKpiStrip';
import {
  SectionHeading, PayoffPathSection, PlanWorkHarderSection, PayoffWinMount,
} from '@/components/liabilities/DebtPlanSections';
import { fetchPosition, fetchPositionHistory, getLastPositionError } from '@/lib/positionApi';
import {
  CashPlanProvider, CashSummaryStrip, CashPlanHero, DecisionsSection,
  MoneyJobsSection, ConnectedSavingsSection,
} from '@/components/cash/CashPlanSections';
import CashInventory from '@/components/cash/CashInventory';

// Phase 86.H — Liabilities sub-tab → the scope the debt plan is recomputed for.
// The backend owns the kind mapping (lib/debt_plan.DEBT_GROUPS).
const LIABILITY_GROUP = {
  'credit-card': 'credit_card',
  loan: 'loan',
  mortgage: 'mortgage',
};


const TOP_TABS_BASE = [
  { id: 'overview',    label: 'Overview' },
  { id: 'cash',        label: 'Cash & Savings' },
  { id: 'investments', label: 'Investments' },
  { id: 'liabilities', label: 'Liabilities' },
  // Note: Recurring moved out of Accounts into a top-level sidebar item
  // (see /app/frontend/src/pages/RecurringPage.js). Deep-link URLs like
  // /accounts?tab=recurring redirect to /recurring in App.js.
];

const VALID_TABS = new Set(TOP_TABS_BASE.map((t) => t.id));

/**
 * Phase 86 — the local `computeCurrentNetWorth()` helper was deleted. Net worth
 * has exactly one implementation now: `lib/position.py` on the server, surfaced
 * via GET /api/position. A second client-side calculator is how /accounts came
 * to display two different asset totals in the first place.
 */

export default function Accounts() {
  usePageTitle('Accounts');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { profile } = useAuth();
  // Phase 84.22.10 (Wave B3) — currency context is the source of truth.
  // Every child component reads from the same context directly; we no
  // longer prop-drill `convertToBase` / `hasRateFor` through the tree.
  const currencyCtx = useCurrencyContext();
  const baseCurrency = currencyCtx.isReady
    ? currencyCtx.base
    : (profile?.base_currency || profile?.currency || 'INR');

  // Live data
  const {
    accounts: rawAccounts, loading,
    createRow, updateRow, deleteRow, setBalanceManually,
  } = useAccountsData();

  // Phase 84.20.7 (Bug 4) — Overlay `next_payment_date` +
  // `next_payment_amount` for credit-card accounts from the latest
  // applicable parse_job. Nothing writes these fields to the `accounts`
  // table on statement commit, so without this overlay the LiabilityDetail
  // panel shows "—" for both.
  const [liabilitySchedules, setLiabilitySchedules] = useState({});
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const t = session?.access_token;
        if (!t) return;
        const resp = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/accounts/liability-schedule`,
          { headers: { Authorization: `Bearer ${t}` }, timeout: 8000 },
        );
        if (cancelled) return;
        setLiabilitySchedules(resp.data?.schedules || {});
      } catch (e) {
        console.warn('[accounts] liability-schedule overlay failed:', e?.message);
      }
    })();
    return () => { cancelled = true; };
  }, [rawAccounts.length]);

  const accounts = useMemo(() => {
    if (!Object.keys(liabilitySchedules).length) return rawAccounts;
    return rawAccounts.map((a) => {
      const s = liabilitySchedules[a.id];
      if (!s) return a;
      return {
        ...a,
        // Only overlay when the field is currently null — never overwrite
        // a user-authored value already living on the accounts row.
        next_payment_date:   a.next_payment_date   || s.next_payment_date   || null,
        next_payment_amount: a.next_payment_amount || s.next_payment_amount || null,
      };
    });
  }, [rawAccounts, liabilitySchedules]);

  // Section / detail state.
  // Legacy deep-link support: /accounts?tab=recurring now points to a
  // top-level route. If we detect that param, redirect immediately.
  useEffect(() => {
    if (searchParams.get('tab') === 'recurring') {
      navigate('/recurring', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const initialTab = VALID_TABS.has(searchParams.get('tab')) ? searchParams.get('tab') : 'overview';
  const [section, setSection] = useState(initialTab);
  const [selectedAccount, setSelectedAccount] = useState(null);
  // Phase 86.L — the balance and delete dialogs used to reuse `selectedAccount`,
  // which ALSO renders the account detail view. Opening "Update balance" from a
  // list row therefore silently switched the page to that account's detail, and
  // you only noticed once the dialog closed. Dialogs get their own state.
  const [dialogAccount, setDialogAccount] = useState(null);
  // Phase 86.H — the active Liabilities sub-tab scopes the whole page.
  const [liabFilter, setLiabFilter] = useState('all');

  // CRUD dialog state
  const [formOpen,     setFormOpen]     = useState(false);
  const [formCategory, setFormCategory] = useState('cash');
  // Phase 86.K — "Add cash or wallet" should open the form already on the right
  // type, so the two sub-tabs each have a CTA that does what it says.
  const [formPresetType, setFormPresetType] = useState(null);
  const [formAccount,  setFormAccount]  = useState(null); // null = create
  const [balOpen, setBalOpen] = useState(false);
  const [delOpen, setDelOpen] = useState(false);

  // Keep URL in sync when the section changes (deep-linkable).
  useEffect(() => {
    const current = searchParams.get('tab');
    if (section === 'overview' && current) {
      setSearchParams({}, { replace: true });
    } else if (section !== 'overview' && current !== section) {
      setSearchParams({ tab: section }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section]);

  // Respond to URL changes (back/forward navigation).
  useEffect(() => {
    const t = searchParams.get('tab');
    if (VALID_TABS.has(t) && t !== section) setSection(t);
    else if (!t && section !== 'overview') setSection('overview');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const topTabs = useMemo(() => TOP_TABS_BASE.map((t) => {
    if (t.id === 'overview') return t;
    return { ...t, count: accounts.filter((a) => a.category === t.id).length };
  }), [accounts]);

  const handleSelectAccount = (a) => {
    setSelectedAccount(a);
    if (a.category === 'cash')             setSection('cash');
    else if (a.category === 'investments') setSection('investments');
    else if (a.category === 'liabilities') setSection('liabilities');
  };
  const handleBack = () => setSelectedAccount(null);

  const linkAccount = () => navigate('/import');

  // Live price refresh — hits /api/wealth/refresh-prices which re-values
  // every auto-update-eligible holding (MFs via cached AMFI directory,
  // stocks via live Yahoo). 60s server-side cooldown enforced.
  const [refreshingPrices, setRefreshingPrices] = useState(false);
  const refreshPrices = async () => {
    if (refreshingPrices) return;
    setRefreshingPrices(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/wealth/refresh-prices`,
        null,
        { headers: { Authorization: `Bearer ${session?.access_token || ''}` }, timeout: 30000 },
      );
      const { refreshed = 0 } = r.data || {};
      toast.success(refreshed > 0
        ? `Refreshed ${refreshed} holding${refreshed === 1 ? '' : 's'}.`
        : 'Nothing to refresh yet — add a quantity + symbol on an MF or stock to enable auto-pricing.');
      // The values on-screen come from useAccountsData; nudge a reload
      // so the freshly-priced values surface immediately.
      window.dispatchEvent(new Event('accounts:refresh'));
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || err?.message || 'Could not refresh prices.');
    } finally {
      setRefreshingPrices(false);
    }
  };

  /* -------- CRUD action handlers -------- */

  // The cash plan's nudges know which account is holding a figure back, but not
  // how to open the dialog — so they hand back an id.
  const openSetBalanceById = (id) => {
    const match = accounts.find((a) => String(a.id) === String(id));
    if (match) openSetBalance(match);
  };

  const openAdd = (category, presetType = null) => {
    setFormCategory(category);
    setFormAccount(null);
    setFormPresetType(presetType);
    setFormOpen(true);
  };
  const openEdit = (account) => {
    setFormCategory(account.category);
    setFormAccount(account);
    setFormOpen(true);
  };
  const openSetBalance = (account) => {
    setDialogAccount(account);
    setBalOpen(true);
  };
  const openDelete = (account) => {
    setDialogAccount(account);
    setDelOpen(true);
  };
  const confirmDelete = async (account) => {
    await deleteRow(account.source_table, account.id);
    // If we deleted the currently-open detail, close it.
    if (selectedAccount && selectedAccount.id === account.id) setSelectedAccount(null);
    window.dispatchEvent(new Event('accounts:refresh'));
  };

  // Phase 86 — any write changes the canonical position, so every mutation
  // nudges the same refresh event the position reader listens for.
  const notifyChanged = () => window.dispatchEvent(new Event('accounts:refresh'));
  const createAndRefresh = async (...args) => {
    const r = await createRow(...args); notifyChanged(); return r;
  };
  const updateAndRefresh = async (...args) => {
    const r = await updateRow(...args); notifyChanged(); return r;
  };
  const setBalanceAndRefresh = async (...args) => {
    const r = await setBalanceManually(...args); notifyChanged(); return r;
  };

  /* -------- Overview aggregations from real data -------- */

  // Phase 86 — the canonical position. Every headline, subtotal and chart on
  // the Overview tab reads from this one response, so nothing can drift.
  const [position, setPosition]               = useState(null);
  const [positionHistory, setPositionHistory] = useState(null);
  const [positionLoading, setPositionLoading] = useState(true);
  // Captured so the page can say WHY the position is missing. Without this the
  // banner was unactionable — and in production we can't read the logs.
  const [positionError, setPositionError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async (fresh = false) => {
      setPositionLoading(true);
      const [p, h] = await Promise.all([fetchPosition({ fresh }), fetchPositionHistory()]);
      setPositionError(p ? null : getLastPositionError());
      if (cancelled) return;
      setPosition(p);
      setPositionHistory(h);
      setPositionLoading(false);
    };
    load();
    // Re-read after any add / edit / delete. `useAccountsData` already emits
    // this event, so we reuse it rather than re-fetching on every list change
    // (which fired the expensive resolver twice on first paint).
    const onRefresh = () => { load(true); };
    window.addEventListener('accounts:refresh', onRefresh);
    window.addEventListener('accounts:changed', onRefresh);
    return () => {
      cancelled = true;
      window.removeEventListener('accounts:refresh', onRefresh);
      window.removeEventListener('accounts:changed', onRefresh);
    };
  }, []);

  // Attention items = liabilities with a next_payment_date in the next 30 days
  const attentionItems = useMemo(() => {
    const now = Date.now();
    const soon = now + 30 * 24 * 3600 * 1000;
    return accounts
      .filter((a) => a.category === 'liabilities' && a.next_payment_date)
      .map((a) => {
        const t = new Date(a.next_payment_date).getTime();
        if (isNaN(t) || t > soon) return null;
        return {
          label: `${a.name} payment due`,
          sub: `Due ${new Date(a.next_payment_date).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}${a.next_payment_amount ? ` · ${a.next_payment_amount.toLocaleString('en-IN')}` : ''}`,
          urgency: t < now + 7 * 24 * 3600 * 1000 ? 'high' : 'medium',
        };
      })
      .filter(Boolean);
  }, [accounts]);

  /* -------- Render -------- */

  return (
    <div className="max-w-[1240px] mx-auto px-6 lg:px-10 pt-6 sm:pt-8 pb-16" data-testid="accounts-page">
      {/* Page header */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-2xl md:text-[28px] text-charcoal font-medium leading-tight" data-testid="accounts-page-title">
            Accounts
          </h1>
          <p className="text-muted-foreground text-sm mt-1">Your complete financial picture in one place.</p>
        </div>
        <div className="flex items-center gap-2">
          {section === 'investments' && (
            <Button
              size="sm"
              variant="ghost"
              onClick={refreshPrices}
              disabled={refreshingPrices}
              className="text-charcoal/80 hover:text-charcoal"
              data-testid="accounts-refresh-prices"
            >
              {refreshingPrices
                ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                : <RefreshCcw className="h-3.5 w-3.5 mr-1.5" />}
              Refresh prices
            </Button>
          )}
          {section !== 'overview' && (
            <Button
              size="sm"
              onClick={() => openAdd(section)}
              data-testid="accounts-header-add"
              className="hidden sm:inline-flex"
            >
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              Add
            </Button>
          )}
          <button
            type="button"
            onClick={linkAccount}
            className="flex items-center gap-1.5 text-sm text-charcoal/80 hover:text-charcoal font-medium px-3.5 py-2 rounded-lg border border-border hover:border-charcoal/20 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            data-testid="accounts-import-statement"
          >
            <Upload className="h-3.5 w-3.5" />
            Import statement
          </button>
        </div>
      </div>

      {/* Top-level tabs */}
      <div className="flex items-center gap-0 border-b border-border/60 mb-6 overflow-x-auto" role="tablist">
        {topTabs.map((t) => {
          const isActive = section === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => { setSection(t.id); setSelectedAccount(null); }}
              className={
                'flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-colors relative whitespace-nowrap ' +
                (isActive ? 'text-evergreen' : 'text-muted-foreground hover:text-foreground')
              }
              role="tab"
              aria-selected={isActive}
              data-testid={`accounts-top-tab-${t.id}`}
            >
              {t.label}
              {t.count !== undefined && (
                <span
                  className={
                    'text-[10px] font-medium px-1.5 py-0.5 rounded-full ' +
                    (isActive ? 'bg-evergreen/10 text-evergreen' : 'bg-muted text-muted-foreground')
                  }
                >
                  {t.count}
                </span>
              )}
              {isActive && <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-evergreen rounded-t-sm" />}
            </button>
          );
        })}
      </div>

      {/* Body */}
      {loading && accounts.length === 0 ? (
        <div className="flex items-center gap-2 py-12 text-muted-foreground text-sm justify-center" data-testid="accounts-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading your accounts&hellip;
        </div>
      ) : selectedAccount ? (
        selectedAccount.category === 'investments' ? (
          <InvestmentDetail
            account={selectedAccount}
            onBack={handleBack}
            baseCurrency={baseCurrency}
            onEdit={() => openEdit(selectedAccount)}
            onDelete={() => openDelete(selectedAccount)}
            onSetBalance={() => openSetBalance(selectedAccount)}
          />
        ) : selectedAccount.category === 'liabilities' ? (
          <LiabilityDetail
            account={selectedAccount}
            onBack={handleBack}
            baseCurrency={baseCurrency}
            onEdit={() => openEdit(selectedAccount)}
            onDelete={() => openDelete(selectedAccount)}
            onSetBalance={() => openSetBalance(selectedAccount)}
          />
        ) : (
          <CashDetail
            account={selectedAccount}
            onBack={handleBack}
            baseCurrency={baseCurrency}
            onEdit={() => openEdit(selectedAccount)}
            onDelete={() => openDelete(selectedAccount)}
            onSetBalance={() => openSetBalance(selectedAccount)}
          />
        )
      ) : section === 'overview' ? (
        <OverviewTab
          accounts={accounts}
          position={position}
          history={positionHistory}
          positionLoading={positionLoading}
          positionError={positionError}
          attentionItems={attentionItems}
          baseCurrency={baseCurrency}
          onSelectAccount={handleSelectAccount}
        />
      ) : section === 'cash' ? (
        <CashPlanProvider onUpdateBalance={openSetBalanceById}>
        {/* Phase 86.I Pass B — the four numbers first, then what you have, then
            the plan built from it, then the decisions and the assignments. Every
            section reads one shared /api/cash-plan. No spending chart here. */}
        <CashSummaryStrip />

        <SectionHeading
          title="What you have"
          subtitle="Reported balances in each account's own currency; planning follows below."
          testid="what-you-have-heading"
          tight
        />
        <CashInventory
          accounts={accounts}
          baseCurrency={baseCurrency}
          onAdd={(type) => openAdd('cash', type)}
          onSelectAccount={handleSelectAccount}
          onSetBalance={openSetBalance}
        />

        <CashPlanHero />

        <DecisionsSection />

        <MoneyJobsSection />

        <ConnectedSavingsSection />
        </CashPlanProvider>
      ) : section === 'investments' ? (
        <CategoryTab
          category="investments"
          accounts={accounts}
          baseCurrency={baseCurrency}
          addLabel="Add investment"
          onAdd={() => openAdd('investments')}
          onSelectAccount={handleSelectAccount}
          subTabs={[
            { id: 'all',              label: 'All',       count: accounts.filter((a) => a.category === 'investments').length },
            { id: 'brokerage',        label: 'Brokerage', count: accounts.filter((a) => a.category === 'investments' && a.type === 'brokerage').length },
            { id: 'funds',            label: 'Funds',     count: accounts.filter((a) => a.category === 'investments' && a.type === 'funds').length },
            { id: 'other-investment', label: 'Other',     count: accounts.filter((a) => a.category === 'investments' && a.type === 'other-investment').length },
          ]}
        />
      ) : section === 'liabilities' ? (
        <DebtPlanProvider group={LIABILITY_GROUP[liabFilter] || ''}>
        {/* Phase 86.E Pass B — order set by the user: the four numbers first,
            then what you owe, then the plan built from it, then everything that
            makes the plan work harder. Every section reads one shared
            /api/debt-plan, scoped to the active sub-tab (Phase 86.H). */}
        <DebtKpiStrip />

        <SectionHeading
          title="What you owe"
          subtitle="Reported balances first; planning and scenarios follow below."
          testid="what-you-owe-heading"
          tight
        />
        <CategoryTab
          category="liabilities"
          accounts={accounts}
          baseCurrency={baseCurrency}
          addLabel="Add liability"
          onAdd={() => openAdd('liabilities')}
          onSelectAccount={handleSelectAccount}
          onFilterChange={setLiabFilter}
          subTabs={[
            { id: 'all',             label: 'All',          count: accounts.filter((a) => a.category === 'liabilities').length },
            { id: 'credit-card',     label: 'Credit Cards', count: accounts.filter((a) => a.category === 'liabilities' && a.type === 'credit-card').length },
            { id: 'loan',            label: 'Loans',        count: accounts.filter((a) => a.category === 'liabilities' && a.type === 'loan').length },
            { id: 'mortgage',        label: 'Mortgages',    count: accounts.filter((a) => a.category === 'liabilities' && a.type === 'mortgage').length },
          ]}
        />

        <PayoffPathSection />

        <PlanWorkHarderSection />

        {/* v3.7.5 Phase 84.29 — Debt Focus ranking. Uses the normalised account
            shape so it works uniformly across rows sourced from `accounts`
            (credit cards excluded) and `liabilities`. Renders itself null when
            there are no ranked debts. */}
        <DebtFocusTable
          items={accounts
            .filter((a) => a.category === 'liabilities' && a.type !== 'credit-card')
            .filter((a) => liabFilter === 'all' || a.type === liabFilter)
            .map((a) => ({
              id: a.id,
              name: a.name,
              kind: a.kind || a.type || 'other_debt',
              interest_rate: a.interest_rate,
              outstanding: Math.abs(Number(a.balance || 0)),
              emi_amount: a.next_payment_amount || a.minimum_payment,
              currency: a.currency,
            }))}
          currency={baseCurrency}
        />

        <PayoffWinMount />
        </DebtPlanProvider>
      ) : null}

      {/* Dialogs */}
      <AccountFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        category={formCategory}
        account={formAccount}
        presetType={formPresetType}
        onCreate={createAndRefresh}
        onUpdate={updateAndRefresh}
        baseCurrency={baseCurrency}
      />
      <SetBalanceDialog
        open={balOpen}
        onOpenChange={setBalOpen}
        account={dialogAccount}
        onSubmit={setBalanceAndRefresh}
      />
      <DeleteAccountDialog
        open={delOpen}
        onOpenChange={setDelOpen}
        account={dialogAccount}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
